import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface Language {
  code: string;
  name: string; // Native name (endonym) - language name in its own language
  flag: string; // Emoji flag or image path
}

// Language names in their native languages (endonyms)
const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },      // English in English
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },      // German in German
  { code: 'es', name: 'Español', flag: '🇪🇸' },      // Spanish in Spanish
  { code: 'fr', name: 'Français', flag: '🇫🇷' },     // French in French
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },        // Finnish in Finnish
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },       // Turkish in Turkish
];

const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current language, handling language codes like 'tr-TR' -> 'tr'
  const currentLangCode = i18n.language.split('-')[0].toLowerCase();
  const currentLanguage = languages.find(lang => lang.code === currentLangCode) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLanguageChange = async (langCode: string) => {
    // CRITICAL: Change language without page reload or navigation
    // This preserves ALL state:
    // - React component state
    // - Form data (unsaved changes)
    // - Current page/route
    // - Scroll position
    // - Modal states
    // - Any unfinished operations
    
    // i18n.changeLanguage() is async but does NOT cause:
    // - Page reloads
    // - Navigation
    // - State loss
    // - Component unmounting
    
    try {
      // Cookie is set by i18n languageChanged handler when we changeLanguage below
      
      // Change the language (i18n will persist to cookie)
      await i18n.changeLanguage(langCode);
      
      // Language changed successfully - all state preserved
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
      // Even on error, don't navigate or reload
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
        aria-label="Select language"
      >
        <span className="text-xl">{currentLanguage.flag}</span>
        <span className="text-sm font-medium hidden sm:inline">{currentLanguage.name}</span>
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[60] overflow-hidden">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                currentLangCode === lang.code
                  ? 'bg-gray-100 dark:bg-gray-700 font-medium'
                  : ''
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-sm text-gray-900 dark:text-gray-100">{lang.name}</span>
              {currentLangCode === lang.code && (
                <span className="ml-auto text-blue-600 dark:text-blue-400">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
